import { useEffect, useState } from 'react'

import { api } from '../../lib/api'
import type { Round } from '../../lib/approvals'
import { roundLabel } from '../../lib/approvals'
import { MeHead, RoundPicker, dayLabel, dowLabel, useMeData } from './parts'

/* ============================================================================
   เวลาทำงานของฉัน — `/me/worktime`
   API: GET /api/me/worktime?round=YYYY/MM · backend/src/routes/me.js → lib/worktime.js
        + GET /api/me/ot?round=YYYY/MM (คอลัมน์ OT ต่อวัน) · lib/ot.js

   รูปแบบยกมาจากหน้า "เวลาเข้าออก พนักงาน" ของ simat_label (hrm-admin.simatlabel.co.th):
   การ์ดสรุปด้านบน + ตารางรายวันที่เทียบ "กำหนด" กับ "ตอกจริง" แล้วบอกสถานะเป็น badge

   ── กรองด้วย "รอบ" ไม่ใช่เดือนปฏิทิน ─────────────────────────────────────────
   สาย/ขาด/ลา ถูกตัดเข้าเงินเดือนตามรอบวันที่ 21 ถึง 20 (ตาราง HR_OT_PERIOD)
   เดือนปฏิทินทำให้ยอดบนหน้าจอไม่ตรงกับสลิป · รายการรอบมากับ response (`rounds`)

   ⚠️ สองคำขอนี้ต้องใช้รอบเดียวกันเสมอ (ตัวแปร query ตัวเดียวกัน) ไม่งั้นคอลัมน์ OT
      จะเป็นของอีกรอบแล้วไม่โชว์อะไรเลยทั้งที่มีข้อมูล

   ⚠️ นี่คือหน้าของ **คนที่กำลังดูอยู่** — user ธรรมดาเห็นของตัวเอง, admin ที่กด
      "เข้าแทน" (delegate) เห็นของคนที่แทน · backend อ่าน empCode จาก session เท่านั้น
      (ดู me.js) เลยไม่มีช่องเลือกพนักงานในหน้านี้ — ตั้งใจเพื่อความปลอดภัย

   คอลัมน์ "ตอก" กับ "นับ" ต้องแยกกันเสมอ (เหมือนหน้า /admin/reports/person):
   ตอก = เวลาที่เครื่องสแกนบันทึก · นับ = เวลาที่เอาไปคิดเงินเดือนหลัง HR แก้แล้ว
   ============================================================================ */

type Leave = { leaveTypeDesc: string; from: string | null; to: string | null }
type TimeEdit = {
  from: string | null
  to: string | null
  toNextDay?: boolean
  approved: boolean
  statusText: string
}

type Day = {
  workDate: string
  /** 0 = อาทิตย์ (getUTCDay ฝั่ง backend) ไม่ใช่ชื่อวัน */
  dow: number
  isHoliday: boolean
  noTimeRequired: boolean
  inProgress: boolean
  isFuture: boolean
  shiftStart: string | null
  shiftEnd: string | null
  punchIn: string | null
  punchOut: string | null
  inTime: string | null
  outTime: string | null
  outDayOffset: number
  lateMinute: number
  late: boolean
  missingIn: boolean
  missingOut: boolean
  absent: boolean
  onLeave: boolean
  leaveTypeDesc: string | null
  leaves: Leave[]
  timeEdits: TimeEdit[]
  workDayRemark: string | null
}

type Data = {
  round: Round
  rounds: Round[]
  range: { from: string; to: string }
  employee: { empCode: string; empName: string | null }
  days: Day[]
  summary: Record<string, number>
}

type OtItem = {
  otDate: string
  timeStart: string | null
  timeEnd: string | null
  statusText: string
  statusGroup: string
}
type OtData = { items: OtItem[] }

/** นาที → 'H ชม. M น.' (การ์ดสายรวม) · 0 = — */
function minutesText(min: number): string {
  if (!min || min <= 0) return '—'
  const h = Math.floor(min / 60)
  const m = min % 60
  return h ? `${h} ชม.${m ? ` ${m} น.` : ''}` : `${m} น.`
}

/** สถานะของวันหนึ่ง → ป้าย + โทนสี (ไล่ลำดับความสำคัญเหมือนคอลัมน์กะในหน้า admin)
 *
 * ⚠️ "ยังไม่ถึง" ใช้เฉพาะวันที่ยังมาไม่ถึงจริง (d.isFuture) เท่านั้น — backend แสดง
 *    ครบทั้งเดือนแล้ว วันอนาคตยังไม่เกิดอะไร ต้องเช็กก่อนทุกสถานะ (ยกเว้นวันหยุด
 *    ที่รู้ล่วงหน้าได้) ไม่งั้นจะขึ้น "ตรงเวลา" ทั้งที่ยังไม่ถึงวัน
 * ⚠️ "สาย" มาก่อน missingOut — "วันนี้" ที่มาสายแต่ยังไม่ตอกออก (d.inProgress) มี
 *    d.late=true อยู่แล้ว ต้องขึ้น "สาย" ไม่ใช่กลบด้วยสถานะอื่น (เคสจริง 2034 28 ต.ค.) */
function dayStatus(d: Day): { label: string; tone: string } | null {
  if (d.isHoliday) return { label: 'วันหยุด', tone: 'chip--off' }
  if (d.isFuture) return { label: 'ยังไม่ถึง', tone: 'chip--off' }
  if (d.onLeave) return { label: d.leaveTypeDesc ?? 'ลา', tone: 'chip--warn' }
  if (d.noTimeRequired) return { label: 'ไม่ต้องบันทึกเวลา', tone: 'chip' }
  if (d.absent) return { label: 'ขาดงาน', tone: 'chip--bad' }
  if (d.late) return { label: 'สาย', tone: 'chip--warn' }
  if (d.missingIn || d.missingOut)
    return { label: d.missingOut ? 'ไม่มีเวลาออก' : 'ไม่มีเวลาเข้า', tone: 'chip--warn' }
  return { label: 'ตรงเวลา', tone: 'chip--ok' }
}

export default function MyWorktime() {
  const [round, setRound] = useState('')
  const query = round ? `?round=${encodeURIComponent(round)}` : ''
  const { data, loading, error, reload } = useMeData<Data>('worktime', query)

  // OT ต่อวัน — ดึงรอบเดียวกัน (frontend เท่านั้น ไม่แตะ backend) · เงียบถ้าโหลดไม่ได้
  const [otByDate, setOtByDate] = useState<Map<string, OtItem[]>>(new Map())
  useEffect(() => {
    let alive = true
    api<OtData>(`/api/me/ot${query}`)
      .then((d) => {
        if (!alive) return
        const map = new Map<string, OtItem[]>()
        for (const o of d.items) {
          const list = map.get(o.otDate) ?? []
          list.push(o)
          map.set(o.otDate, list)
        }
        setOtByDate(map)
      })
      .catch(() => alive && setOtByDate(new Map()))
    return () => {
      alive = false
    }
  }, [query])

  const s = data?.summary ?? {}
  const cards = [
    { label: 'มาทำงาน', value: s.completeDays ?? 0, suffix: 'วัน', tone: 'ok' },
    { label: 'วันหยุด', value: s.holidays ?? 0, suffix: 'วัน', tone: 'default' },
    { label: 'ขาดงาน', value: s.absentDays ?? 0, suffix: 'วัน', tone: 'bad' },
    { label: 'สาย', value: s.lateDays ?? 0, suffix: 'วัน', tone: 'warn' },
    { label: 'สายรวม', value: minutesText(s.lateMinutes ?? 0), tone: 'warn' },
    { label: 'ลา', value: s.leaveDays ?? 0, suffix: 'วัน', tone: 'warn' },
    { label: 'ปรับปรุงเวลา', value: s.adjustedDays ?? 0, suffix: 'วัน', tone: 'default' },
    { label: 'สแกนไม่ครบ', value: s.missingDays ?? 0, suffix: 'วัน', tone: 'warn' },
  ]

  return (
    <>
      <MeHead
        title="เวลาทำงานของฉัน"
        meta={data ? `${data.employee.empName ?? ''} · ${data.employee.empCode}` : undefined}
        loading={loading}
        error={error}
        onReload={reload}
      >
        {data && (
          <RoundPicker
            value={data.round.roundId}
            rounds={data.rounds}
            onChange={setRound}
            disabled={loading}
          />
        )}
      </MeHead>

      {data && (
        <section className="card">
          <div className="section-head">
            <h2>{roundLabel(data.round)}</h2>
          </div>

          <div className="wt-cards">
            {cards.map((c) => (
              <div key={c.label} className={`wt-card wt-card--${c.tone}`}>
                <div className="wt-card__label">{c.label}</div>
                <div className="wt-card__value">
                  {c.value}
                  {c.suffix && <span className="wt-card__suffix"> {c.suffix}</span>}
                </div>
              </div>
            ))}
          </div>

          {data.days.length === 0 ? (
            <p className="meta">ไม่มีข้อมูลในรอบ {roundLabel(data.round)}</p>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="nowrap">วันที่</th>
                    <th>วัน</th>
                    <th className="nowrap">กำหนด (เข้า–ออก)</th>
                    <th className="nowrap">ตอกจริง (เข้า–ออก)</th>
                    <th className="num">สาย</th>
                    <th>สถานะ</th>
                    <th>การลา</th>
                    <th>OT</th>
                    <th>ปรับปรุงเวลา</th>
                  </tr>
                </thead>
                <tbody>
                  {data.days.map((d) => {
                    const st = dayStatus(d)
                    const ots = otByDate.get(d.workDate) ?? []
                    const weekend = d.dow === 0 || d.dow === 6
                    return (
                      <tr
                        key={d.workDate}
                        className={
                          d.isHoliday
                            ? 'wt-row--holiday'
                            : d.isFuture
                              ? 'wt-row--future'
                              : weekend
                                ? 'wt-row--weekend'
                                : undefined
                        }
                      >
                        <td className="nowrap">{dayLabel(d.workDate)}</td>
                        <td className="meta">{dowLabel(d.dow)}</td>
                        <td className="nowrap meta">
                          {d.shiftStart && d.shiftEnd ? `${d.shiftStart}–${d.shiftEnd}` : '—'}
                        </td>
                        <td className="nowrap">
                          {d.punchIn ?? '—'}
                          {d.punchIn || d.punchOut ? ' – ' : ''}
                          {d.punchOut ?? (d.punchIn ? '—' : '')}
                          {d.outDayOffset > 0 && <span className="chip">วันถัดไป</span>}
                          {/* "นับ" ต่างจาก "ตอก" เมื่อ HR แก้เวลาให้แล้วเท่านั้น */}
                          {((d.inTime && d.inTime !== d.punchIn) ||
                            (d.outTime && d.outTime !== d.punchOut)) && (
                            <span className="cell-note">
                              นับ {d.inTime ?? '—'} – {d.outTime ?? '—'}
                            </span>
                          )}
                        </td>
                        <td className="num">{d.lateMinute ? minutesText(d.lateMinute) : '—'}</td>
                        <td>{st ? <span className={st.tone}>{st.label}</span> : '—'}</td>
                        <td>
                          {d.onLeave && d.leaves.length > 0 ? (
                            d.leaves.map((lv, i) => (
                              <div key={i} className="cell-note">
                                {lv.leaveTypeDesc}
                                {lv.from || lv.to ? ` ${lv.from ?? ''}–${lv.to ?? ''}` : ''}
                              </div>
                            ))
                          ) : (
                            <span className="meta">—</span>
                          )}
                        </td>
                        <td>
                          {ots.length > 0 ? (
                            ots.map((o, i) => (
                              <div key={i} className="cell-note">
                                {o.timeStart ?? '—'}–{o.timeEnd ?? '—'}{' '}
                                <span className="meta">({o.statusText})</span>
                              </div>
                            ))
                          ) : (
                            <span className="meta">—</span>
                          )}
                        </td>
                        <td>
                          {d.timeEdits.length > 0 ? (
                            d.timeEdits.map((te, i) => (
                              <div key={i} className="cell-note">
                                {te.from ?? '—'}–{te.to ?? '—'}
                                {te.toNextDay ? ' (วันถัดไป)' : ''}
                                {!te.approved ? ` · ${te.statusText}` : ''}
                              </div>
                            ))
                          ) : (
                            <span className="meta">—</span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </>
  )
}
